import { apyToAprPercent } from '../../../utils/parsing'
import { YieldFetcher } from '../../types'
import { fetchDefiLlamaApy } from './defillama'

/**
 * Strata (docs.strata.markets) — senior/junior risk tranches over
 * yield-bearing dollars, Ethereum-only. Each market's CDO pools a
 * collateral asset (Ethena sUSDe, Neutrl sNUSD, Midas mHYPER / mM1-USD,
 * Saturn sUSDat, Hastra PRIME) and splits its realized yield: the
 * senior (`sr*`) earns a benchmark-floored rate, the junior (`jr*`)
 * takes the residual as levered upside and first-loss capital.
 *
 * Rate source: Strata's own S3 analytics feed, one JSON covering every
 * market. Per CDO the `week` object carries the trailing-7-day realized
 * tranche rates as BOTH `apr` and `apy` — `apr` is nominal, matching
 * the package convention directly (no conversion; validated against
 * DefiLlama's independently measured `strata-markets` pools, which
 * agree within noise on all 12 tranches). Per-pool DefiLlama is the
 * fallback, APY → APR converted.
 *
 * Junior caveat: these are trailing REALIZED rates on a first-loss
 * position — negative when the collateral yield undershoots the senior
 * benchmark, and absurdly positive while a marked-down collateral
 * recovers (jrUSDat printed a 4-digit trailing APR bouncing back from
 * the 2026-06/07 sUSDat drawdown that had marked it down to ~0.36 —
 * Saturn's VAULT fell 22.8 %, marked to STRC; USDat kept its peg
 * throughout, see SATURN.md). They are
 * reported verbatim; consumers ranking by rate should read them next
 * to the tranche price.
 */

const STRATA_ANALYTICS_URL =
  'https://s3.strata.money/tranches/analytics-v18.json'

// Yield keys — token-list assetGroups where listed (chain 1; note the
// inconsistent per-token casing, pinned against the live list 2026-08).
// srPRIME/jrPRIME are not in the token list yet — provisional keys in
// the same shape.
export const SRUSDE_KEY = 'Strata Senior USDe::SRUSDE'
export const JRUSDE_KEY = 'Strata Junior USDe::jrUSDe'
export const SRNUSD_KEY = 'Strata Senior NUSD::srNUSD'
export const JRNUSD_KEY = 'Strata Junior NUSD::JRNUSD'
export const SRMHYPER_KEY = 'Strata Senior mHYPER::srmHYPER'
export const JRMHYPER_KEY = 'Strata Junior mHYPER::JRMHYPER'
export const SRMM1USD_KEY = 'Strata Senior mM1-USD::SRMM1-USD'
export const JRMM1USD_KEY = 'Strata Junior mM1-USD::JRMM1-USD'
export const SRUSDAT_KEY = 'Strata Senior USDat::SRUSDAT'
export const JRUSDAT_KEY = 'Strata Junior USDat::JRUSDAT'
export const SRPRIME_KEY = 'Strata Senior PRIME::srPRIME'
export const JRPRIME_KEY = 'Strata Junior PRIME::jrPRIME'

/** Analytics-CDO key → tranche yield keys + DefiLlama fallback pools. */
const STRATA_MARKETS: {
  cdo: string
  sr: { key: string; llamaPool: string }
  jr: { key: string; llamaPool: string }
}[] = [
  {
    cdo: 'ethenaCdo',
    sr: { key: SRUSDE_KEY, llamaPool: '843be062-d836-43ef-9670-c78d6ecb60bf' },
    jr: { key: JRUSDE_KEY, llamaPool: '8352355c-5ad7-45c5-aca2-628de224f8d8' },
  },
  {
    cdo: 'neutrlCdo',
    sr: { key: SRNUSD_KEY, llamaPool: '979711ce-d0c0-4aae-a5fc-d7b76e868649' },
    jr: { key: JRNUSD_KEY, llamaPool: '947928b7-c446-49d7-a378-392df37660f7' },
  },
  {
    cdo: 'mhyperCdo',
    sr: {
      key: SRMHYPER_KEY,
      llamaPool: 'a0560647-eb43-45d9-880d-3155db6d357e',
    },
    jr: {
      key: JRMHYPER_KEY,
      llamaPool: '908664de-79ee-4f4f-9347-40da76a43552',
    },
  },
  {
    cdo: 'mm1usdCdo',
    sr: {
      key: SRMM1USD_KEY,
      llamaPool: '8139ac7b-c983-4800-bb98-60abe3d68157',
    },
    jr: {
      key: JRMM1USD_KEY,
      llamaPool: '2172d4b0-9a76-4452-af46-2ce833d2c612',
    },
  },
  {
    cdo: 'saturnCdo',
    sr: { key: SRUSDAT_KEY, llamaPool: 'd0f93146-6936-4fac-841e-68178b5a59b2' },
    jr: { key: JRUSDAT_KEY, llamaPool: '1f42963b-ff24-4563-acb7-0fd35e6ec45b' },
  },
  {
    cdo: 'figureCdo',
    sr: { key: SRPRIME_KEY, llamaPool: 'c590eff9-9de2-462c-b629-7e9c89f0b8e9' },
    jr: { key: JRPRIME_KEY, llamaPool: 'f88ff950-9096-46c0-bb9e-93f4d8266c41' },
  },
]

type StrataAnalytics = {
  [cdoKey: string]: {
    week?: {
      srt?: { apr?: number }
      jrt?: { apr?: number }
    }
  }
}

const fetchStrataAnalytics = async (): Promise<StrataAnalytics> => {
  const res = await fetch(STRATA_ANALYTICS_URL, {
    headers: { accept: 'application/json' },
    signal: AbortSignal.timeout(8000),
  })
  if (!res.ok) throw new Error(`Strata analytics HTTP ${res.status}`)
  return (await res.json()) as StrataAnalytics
}

export const strataFetcher: YieldFetcher = {
  label: 'STRATA',
  fetch: async () => {
    const analytics = await fetchStrataAnalytics().catch(() => null)
    const out: { [key: string]: number } = {}
    await Promise.all(
      STRATA_MARKETS.flatMap(({ cdo, sr, jr }) =>
        (
          [
            [sr, analytics?.[cdo]?.week?.srt?.apr],
            [jr, analytics?.[cdo]?.week?.jrt?.apr],
          ] as const
        ).map(async ([tranche, apr]) => {
          if (typeof apr === 'number' && Number.isFinite(apr)) {
            out[tranche.key] = apr
            return
          }
          // Negative junior APRs are legitimate — only fall back when
          // the feed is missing the market entirely.
          const llamaApy = await fetchDefiLlamaApy(tranche.llamaPool).catch(
            () => null,
          )
          if (llamaApy !== null) out[tranche.key] = apyToAprPercent(llamaApy)
        }),
      ),
    )
    return out
  },
}
